// Centralised legal content for AutoDistrict (SHLI SRL)
// Stored as plain JS objects to avoid bloating i18n.js
// Last updated: 2026-02-06

export const COMPANY = {
  legalName: 'SHLI SRL',
  brandName: 'AutoDistrict',
  vat: 'BE0703936819',
  address: 'Avenue de Grunne 28, 1970 Wezembeek-Oppem, Belgique',
  email: 'info@shli.be',
  publicEmail: 'info@autodistrict.be',
  phone: '+32 489 43 21 61',
  host: 'OVH SAS — 2 rue Kellermann, 59100 Roubaix, France',
  lastUpdated: '06/02/2026',
};

export const legalNotice = {
  fr: {
    title: 'Mentions légales',
    eyebrow: 'Informations légales',
    sections: [
      {
        heading: 'Éditeur du site',
        body: [
          `Le site ${COMPANY.brandName} est édité par la société ${COMPANY.legalName}, immatriculée à la Banque-Carrefour des Entreprises sous le numéro ${COMPANY.vat}.`,
          `Siège social : ${COMPANY.address}.`,
          `Email : ${COMPANY.email} — Téléphone : ${COMPANY.phone}.`,
          `Numéro de TVA : ${COMPANY.vat}.`,
        ],
      },
      {
        heading: 'Directeur de la publication',
        body: [`Le directeur de la publication est le représentant légal de ${COMPANY.legalName}.`],
      },
      {
        heading: 'Hébergeur',
        body: [`Le site est hébergé par ${COMPANY.host}.`],
      },
      {
        heading: 'Propriété intellectuelle',
        body: [
          `L'ensemble du contenu du site (textes, photographies, logos, graphismes, vidéos, code source) est la propriété exclusive de ${COMPANY.legalName} ou de ses partenaires et est protégé par le droit belge et international de la propriété intellectuelle.`,
          `Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans l'autorisation écrite préalable de ${COMPANY.legalName}, est strictement interdite.`,
        ],
      },
      {
        heading: 'Activité',
        body: [
          `${COMPANY.brandName} est un garage à porte fermée spécialisé dans la vente de véhicules d'occasion sélectionnés. Toutes les visites se font uniquement sur rendez-vous.`,
        ],
      },
      {
        heading: 'Limitation de responsabilité',
        body: [
          `${COMPANY.legalName} met tout en œuvre pour fournir des informations exactes et à jour sur ce site. Toutefois, des erreurs ou omissions peuvent survenir. L'utilisateur reconnaît utiliser les informations sous sa propre responsabilité.`,
          `${COMPANY.legalName} ne saurait être tenue responsable des dommages directs ou indirects résultant de l'accès ou de l'utilisation du site, y compris l'inaccessibilité, les pertes de données, les détériorations ou virus susceptibles d'affecter l'équipement informatique de l'utilisateur.`,
        ],
      },
      {
        heading: 'Liens externes',
        body: [
          `Le site peut contenir des liens vers des sites tiers. ${COMPANY.legalName} n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.`,
        ],
      },
      {
        heading: 'Droit applicable',
        body: [
          `Les présentes mentions légales sont régies par le droit belge. En cas de litige, les tribunaux de l'arrondissement de Bruxelles seront seuls compétents.`,
        ],
      },
      {
        heading: 'Contact',
        body: [
          `Pour toute question concernant ces mentions légales, vous pouvez nous contacter par email à ${COMPANY.email} ou par téléphone au ${COMPANY.phone}.`,
        ],
      },
    ],
    updated: `Dernière mise à jour : ${COMPANY.lastUpdated}.`,
  },
  en: {
    title: 'Legal Notice',
    eyebrow: 'Legal information',
    sections: [
      {
        heading: 'Site publisher',
        body: [
          `The ${COMPANY.brandName} website is published by ${COMPANY.legalName}, registered with the Crossroads Bank for Enterprises under number ${COMPANY.vat}.`,
          `Registered office: ${COMPANY.address}.`,
          `Email: ${COMPANY.email} — Phone: ${COMPANY.phone}.`,
          `VAT number: ${COMPANY.vat}.`,
        ],
      },
      {
        heading: 'Publication director',
        body: [`The publication director is the legal representative of ${COMPANY.legalName}.`],
      },
      {
        heading: 'Hosting provider',
        body: [`The site is hosted by ${COMPANY.host}.`],
      },
      {
        heading: 'Intellectual property',
        body: [
          `All content on this website (texts, photographs, logos, graphics, videos, source code) is the exclusive property of ${COMPANY.legalName} or its partners and is protected by Belgian and international intellectual property law.`,
          `Any reproduction, representation, modification or use, in whole or in part, without the prior written authorisation of ${COMPANY.legalName} is strictly prohibited.`,
        ],
      },
      {
        heading: 'Activity',
        body: [
          `${COMPANY.brandName} is a closed-door garage specialising in the sale of carefully selected pre-owned vehicles. All visits are by appointment only.`,
        ],
      },
      {
        heading: 'Limitation of liability',
        body: [
          `${COMPANY.legalName} strives to provide accurate and up-to-date information on this site. However, errors or omissions may occur. The user acknowledges using the information at their own responsibility.`,
          `${COMPANY.legalName} cannot be held liable for any direct or indirect damages resulting from access to or use of the site, including unavailability, loss of data, damage or viruses that may affect the user's equipment.`,
        ],
      },
      {
        heading: 'External links',
        body: [
          `The site may contain links to third-party sites. ${COMPANY.legalName} has no control over these sites and disclaims any liability for their content.`,
        ],
      },
      {
        heading: 'Applicable law',
        body: [
          `This legal notice is governed by Belgian law. In the event of a dispute, the courts of the Brussels district shall have exclusive jurisdiction.`,
        ],
      },
      {
        heading: 'Contact',
        body: [
          `For any question regarding this legal notice, please contact us by email at ${COMPANY.email} or by phone at ${COMPANY.phone}.`,
        ],
      },
    ],
    updated: `Last updated: ${COMPANY.lastUpdated}.`,
  },
  nl: {
    title: 'Wettelijke vermeldingen',
    eyebrow: 'Wettelijke informatie',
    sections: [
      {
        heading: 'Uitgever van de site',
        body: [
          `De website ${COMPANY.brandName} wordt uitgegeven door ${COMPANY.legalName}, ingeschreven bij de Kruispuntbank van Ondernemingen onder nummer ${COMPANY.vat}.`,
          `Maatschappelijke zetel: ${COMPANY.address}.`,
          `E-mail: ${COMPANY.email} — Telefoon: ${COMPANY.phone}.`,
          `Btw-nummer: ${COMPANY.vat}.`,
        ],
      },
      {
        heading: 'Verantwoordelijke uitgever',
        body: [`De verantwoordelijke uitgever is de wettelijke vertegenwoordiger van ${COMPANY.legalName}.`],
      },
      {
        heading: 'Hosting',
        body: [`De site wordt gehost door ${COMPANY.host}.`],
      },
      {
        heading: 'Intellectuele eigendom',
        body: [
          `Alle inhoud van de website (teksten, foto's, logo's, grafische elementen, video's, broncode) is de exclusieve eigendom van ${COMPANY.legalName} of haar partners en wordt beschermd door het Belgische en internationale recht inzake intellectuele eigendom.`,
          `Elke reproductie, weergave, wijziging of exploitatie, geheel of gedeeltelijk, zonder voorafgaande schriftelijke toestemming van ${COMPANY.legalName}, is strikt verboden.`,
        ],
      },
      {
        heading: 'Activiteit',
        body: [
          `${COMPANY.brandName} is een gesloten garage gespecialiseerd in de verkoop van zorgvuldig geselecteerde tweedehandswagens. Bezoeken vinden uitsluitend op afspraak plaats.`,
        ],
      },
      {
        heading: 'Aansprakelijkheidsbeperking',
        body: [
          `${COMPANY.legalName} stelt alles in het werk om accurate en actuele informatie op deze site te verstrekken. Toch kunnen fouten of weglatingen voorkomen. De gebruiker erkent de informatie onder zijn eigen verantwoordelijkheid te gebruiken.`,
          `${COMPANY.legalName} kan niet aansprakelijk worden gesteld voor directe of indirecte schade als gevolg van toegang tot of gebruik van de site, waaronder onbeschikbaarheid, verlies van gegevens, schade of virussen die de apparatuur van de gebruiker kunnen aantasten.`,
        ],
      },
      {
        heading: 'Externe links',
        body: [
          `De site kan links bevatten naar websites van derden. ${COMPANY.legalName} heeft geen controle over deze sites en wijst elke aansprakelijkheid voor hun inhoud af.`,
        ],
      },
      {
        heading: 'Toepasselijk recht',
        body: [
          `Deze wettelijke vermeldingen worden beheerst door het Belgische recht. In geval van een geschil zijn uitsluitend de rechtbanken van het arrondissement Brussel bevoegd.`,
        ],
      },
      {
        heading: 'Contact',
        body: [
          `Voor elke vraag betreffende deze wettelijke vermeldingen kunt u ons contacteren via e-mail op ${COMPANY.email} of telefonisch op ${COMPANY.phone}.`,
        ],
      },
    ],
    updated: `Laatst bijgewerkt: ${COMPANY.lastUpdated}.`,
  },
};

export const privacyPolicy = {
  fr: {
    title: 'Politique de confidentialité',
    eyebrow: 'Protection des données — RGPD',
    intro: `${COMPANY.legalName} (« nous », « notre ») accorde une importance particulière à la protection de vos données personnelles. Cette politique de confidentialité, conforme au Règlement Général sur la Protection des Données (RGPD — UE 2016/679), décrit les données que nous collectons, la manière dont nous les utilisons et les droits dont vous disposez.`,
    sections: [
      {
        heading: 'Responsable du traitement',
        body: [
          `Le responsable du traitement des données collectées sur ce site est ${COMPANY.legalName}, ${COMPANY.address}.`,
          `Pour toute question relative à vos données personnelles, vous pouvez nous contacter à ${COMPANY.email}.`,
        ],
      },
      {
        heading: 'Données collectées',
        body: [
          `Nous collectons les données strictement nécessaires aux finalités décrites ci-dessous :`,
          `• Identité : nom, prénom, civilité.`,
          `• Coordonnées : email, téléphone, adresse postale (lorsque pertinent).`,
          `• Données véhicule (en cas de demande d'estimation) : marque, modèle, kilométrage, motorisation, prix souhaité, photographies du véhicule, informations complémentaires.`,
          `• Données de navigation : adresse IP, type de navigateur, pages consultées, durée de session — uniquement si vous acceptez les cookies analytiques.`,
        ],
      },
      {
        heading: 'Finalités et bases légales',
        body: [
          `• Gestion des prises de rendez-vous (exécution contractuelle).`,
          `• Traitement des demandes d'estimation et de revente de véhicule (exécution contractuelle / mesures précontractuelles).`,
          `• Réponse à vos demandes via le formulaire de contact (intérêt légitime).`,
          `• Envoi de notre newsletter (consentement explicite, révocable à tout moment).`,
          `• Statistiques d'audience anonymisées (intérêt légitime / consentement pour les cookies analytiques).`,
          `• Respect de nos obligations légales (comptabilité, garantie, sécurité).`,
        ],
      },
      {
        heading: 'Durée de conservation',
        body: [
          `• Demandes d'estimation et de contact : 3 ans à compter du dernier échange.`,
          `• Données liées à une vente : 10 ans (obligations comptables et de garantie).`,
          `• Newsletter : jusqu'au désabonnement.`,
          `• Données de navigation et cookies : 13 mois maximum.`,
        ],
      },
      {
        heading: 'Destinataires des données',
        body: [
          `Vos données sont strictement réservées à un usage interne par les équipes de ${COMPANY.legalName}. Elles peuvent être transmises à nos sous-traitants techniques, dans la stricte mesure nécessaire à l'exécution de leur prestation :`,
          `• Cloudinary (stockage sécurisé des photographies, hébergement Europe/USA).`,
          `• Resend (envoi des notifications email transactionnelles).`,
          `• OVH (hébergement du site web, France).`,
          `• MongoDB Atlas (base de données, hébergement Europe).`,
          `Aucune donnée n'est vendue, louée ou cédée à des tiers à des fins commerciales.`,
        ],
      },
      {
        heading: 'Transferts hors Union européenne',
        body: [
          `Certains sous-traitants peuvent être amenés à traiter vos données en dehors de l'Union européenne. Dans ce cas, les transferts sont encadrés par les Clauses Contractuelles Types de la Commission européenne ou par une décision d'adéquation, garantissant un niveau de protection équivalent.`,
        ],
      },
      {
        heading: 'Vos droits',
        body: [
          `Conformément au RGPD, vous disposez des droits suivants :`,
          `• Droit d'accès, de rectification et d'effacement de vos données.`,
          `• Droit à la limitation et à l'opposition au traitement.`,
          `• Droit à la portabilité.`,
          `• Droit de retirer votre consentement à tout moment.`,
          `• Droit d'introduire une réclamation auprès de l'Autorité de Protection des Données (APD) — www.autoriteprotectiondonnees.be.`,
          `Pour exercer ces droits, contactez-nous à ${COMPANY.email}. Une réponse vous sera apportée dans un délai maximum de 30 jours.`,
        ],
      },
      {
        heading: 'Cookies',
        body: [
          `Notre site utilise des cookies pour assurer son bon fonctionnement, mémoriser vos préférences linguistiques et, sous réserve de votre consentement, mesurer l'audience. Lors de votre première visite, une bannière vous permet d'accepter ou refuser les cookies non essentiels. Vous pouvez modifier votre choix à tout moment en effaçant les cookies de votre navigateur.`,
        ],
      },
      {
        heading: 'Sécurité',
        body: [
          `Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, altération, divulgation ou destruction (chiffrement HTTPS, contrôle d'accès, sauvegardes régulières).`,
        ],
      },
      {
        heading: 'Modification de la politique',
        body: [
          `Cette politique de confidentialité peut être modifiée à tout moment afin de refléter d'éventuelles évolutions légales ou techniques. La date de dernière mise à jour est indiquée en bas de page.`,
        ],
      },
    ],
    updated: `Dernière mise à jour : ${COMPANY.lastUpdated}.`,
  },
  en: {
    title: 'Privacy Policy',
    eyebrow: 'Data protection — GDPR',
    intro: `${COMPANY.legalName} ("we", "our") places particular importance on the protection of your personal data. This privacy policy, compliant with the General Data Protection Regulation (GDPR — EU 2016/679), describes the data we collect, how we use it and the rights you have.`,
    sections: [
      {
        heading: 'Data controller',
        body: [
          `The controller of the data collected on this site is ${COMPANY.legalName}, ${COMPANY.address}.`,
          `For any question regarding your personal data, please contact us at ${COMPANY.email}.`,
        ],
      },
      {
        heading: 'Data collected',
        body: [
          `We only collect data strictly necessary for the purposes described below:`,
          `• Identity: surname, first name, title.`,
          `• Contact details: email, phone, postal address (when relevant).`,
          `• Vehicle data (in case of a valuation request): make, model, mileage, fuel type, expected price, photographs of the vehicle, additional information.`,
          `• Browsing data: IP address, browser type, pages viewed, session duration — only if you accept analytical cookies.`,
        ],
      },
      {
        heading: 'Purposes and legal bases',
        body: [
          `• Managing appointments (contractual performance).`,
          `• Processing valuation and vehicle resale requests (contractual performance / pre-contractual measures).`,
          `• Replying to enquiries submitted through the contact form (legitimate interest).`,
          `• Sending our newsletter (explicit consent, revocable at any time).`,
          `• Anonymised audience statistics (legitimate interest / consent for analytical cookies).`,
          `• Compliance with our legal obligations (accounting, warranty, security).`,
        ],
      },
      {
        heading: 'Retention periods',
        body: [
          `• Valuation and contact requests: 3 years from the last exchange.`,
          `• Sales-related data: 10 years (accounting and warranty obligations).`,
          `• Newsletter: until unsubscription.`,
          `• Browsing data and cookies: 13 months maximum.`,
        ],
      },
      {
        heading: 'Data recipients',
        body: [
          `Your data is strictly reserved for internal use by ${COMPANY.legalName} teams. It may be transmitted to our technical subcontractors, strictly to the extent necessary for their service:`,
          `• Cloudinary (secure storage of photographs, hosting in Europe/USA).`,
          `• Resend (sending transactional email notifications).`,
          `• OVH (website hosting, France).`,
          `• MongoDB Atlas (database, hosting in Europe).`,
          `No data is sold, rented or transferred to third parties for commercial purposes.`,
        ],
      },
      {
        heading: 'Transfers outside the European Union',
        body: [
          `Some subcontractors may process your data outside the European Union. In that case, transfers are framed by the European Commission's Standard Contractual Clauses or by an adequacy decision, ensuring an equivalent level of protection.`,
        ],
      },
      {
        heading: 'Your rights',
        body: [
          `Under the GDPR, you have the following rights:`,
          `• Right of access, rectification and erasure of your data.`,
          `• Right to restriction of and objection to processing.`,
          `• Right to data portability.`,
          `• Right to withdraw your consent at any time.`,
          `• Right to lodge a complaint with the Belgian Data Protection Authority (APD) — www.autoriteprotectiondonnees.be.`,
          `To exercise these rights, contact us at ${COMPANY.email}. We will respond within a maximum of 30 days.`,
        ],
      },
      {
        heading: 'Cookies',
        body: [
          `Our site uses cookies to ensure its proper operation, remember your language preferences and, subject to your consent, measure audience. On your first visit, a banner allows you to accept or refuse non-essential cookies. You can change your choice at any time by clearing your browser cookies.`,
        ],
      },
      {
        heading: 'Security',
        body: [
          `We implement appropriate technical and organisational measures to protect your data against unauthorised access, alteration, disclosure or destruction (HTTPS encryption, access control, regular backups).`,
        ],
      },
      {
        heading: 'Policy updates',
        body: [
          `This privacy policy may be modified at any time to reflect legal or technical changes. The date of the last update is indicated at the bottom of the page.`,
        ],
      },
    ],
    updated: `Last updated: ${COMPANY.lastUpdated}.`,
  },
  nl: {
    title: 'Privacybeleid',
    eyebrow: 'Gegevensbescherming — AVG',
    intro: `${COMPANY.legalName} ("wij", "ons") hecht bijzonder belang aan de bescherming van uw persoonsgegevens. Dit privacybeleid, in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG — EU 2016/679), beschrijft welke gegevens wij verzamelen, hoe wij ze gebruiken en welke rechten u heeft.`,
    sections: [
      {
        heading: 'Verwerkingsverantwoordelijke',
        body: [
          `De verwerkingsverantwoordelijke van de op deze site verzamelde gegevens is ${COMPANY.legalName}, ${COMPANY.address}.`,
          `Voor elke vraag over uw persoonsgegevens kunt u ons contacteren op ${COMPANY.email}.`,
        ],
      },
      {
        heading: 'Verzamelde gegevens',
        body: [
          `Wij verzamelen uitsluitend gegevens die strikt noodzakelijk zijn voor de hieronder beschreven doeleinden:`,
          `• Identiteit: naam, voornaam, aanspreektitel.`,
          `• Contactgegevens: e-mail, telefoon, postadres (indien relevant).`,
          `• Voertuiggegevens (bij een schattingsaanvraag): merk, model, kilometerstand, brandstof, gewenste prijs, foto's van het voertuig, aanvullende informatie.`,
          `• Browsegegevens: IP-adres, browsertype, bezochte pagina's, sessieduur — uitsluitend bij aanvaarding van analytische cookies.`,
        ],
      },
      {
        heading: 'Doeleinden en rechtsgronden',
        body: [
          `• Beheer van afspraken (uitvoering van een overeenkomst).`,
          `• Behandeling van schattings- en wederverkoopaanvragen (uitvoering van een overeenkomst / precontractuele maatregelen).`,
          `• Antwoorden op uw vragen via het contactformulier (gerechtvaardigd belang).`,
          `• Verzending van onze nieuwsbrief (uitdrukkelijke toestemming, op elk moment intrekbaar).`,
          `• Anonieme bezoekersstatistieken (gerechtvaardigd belang / toestemming voor analytische cookies).`,
          `• Naleving van onze wettelijke verplichtingen (boekhouding, waarborg, beveiliging).`,
        ],
      },
      {
        heading: 'Bewaartermijnen',
        body: [
          `• Schattings- en contactaanvragen: 3 jaar vanaf het laatste contact.`,
          `• Verkoopgerelateerde gegevens: 10 jaar (boekhoudkundige en garantieverplichtingen).`,
          `• Nieuwsbrief: tot uitschrijving.`,
          `• Browsegegevens en cookies: maximaal 13 maanden.`,
        ],
      },
      {
        heading: 'Ontvangers van de gegevens',
        body: [
          `Uw gegevens zijn strikt voorbehouden voor intern gebruik door de teams van ${COMPANY.legalName}. Zij kunnen worden doorgegeven aan onze technische onderaannemers, strikt voor zover noodzakelijk voor hun dienstverlening:`,
          `• Cloudinary (beveiligde opslag van foto's, hosting Europa/VS).`,
          `• Resend (verzending van transactionele e-mailmeldingen).`,
          `• OVH (hosting van de website, Frankrijk).`,
          `• MongoDB Atlas (database, hosting in Europa).`,
          `Geen enkele gegevens worden verkocht, verhuurd of overgedragen aan derden voor commerciële doeleinden.`,
        ],
      },
      {
        heading: 'Doorgiften buiten de Europese Unie',
        body: [
          `Sommige onderaannemers verwerken uw gegevens mogelijk buiten de Europese Unie. In dat geval worden doorgiften omkaderd door de Standaardcontractbepalingen van de Europese Commissie of door een adequaatheidsbesluit, zodat een gelijkwaardig beschermingsniveau wordt gewaarborgd.`,
        ],
      },
      {
        heading: 'Uw rechten',
        body: [
          `Onder de AVG beschikt u over de volgende rechten:`,
          `• Recht op inzage, rectificatie en wissing van uw gegevens.`,
          `• Recht op beperking en bezwaar tegen de verwerking.`,
          `• Recht op gegevensoverdraagbaarheid.`,
          `• Recht om uw toestemming op elk moment in te trekken.`,
          `• Recht om een klacht in te dienen bij de Gegevensbeschermingsautoriteit (GBA) — www.gegevensbeschermingsautoriteit.be.`,
          `Om deze rechten uit te oefenen, contacteer ons op ${COMPANY.email}. U ontvangt een antwoord binnen maximaal 30 dagen.`,
        ],
      },
      {
        heading: 'Cookies',
        body: [
          `Onze site gebruikt cookies om de goede werking te verzekeren, uw taalvoorkeuren te onthouden en, mits uw toestemming, het bezoek te meten. Bij uw eerste bezoek kunt u via een banner niet-essentiële cookies aanvaarden of weigeren. U kunt uw keuze op elk moment wijzigen door de cookies van uw browser te wissen.`,
        ],
      },
      {
        heading: 'Beveiliging',
        body: [
          `Wij implementeren passende technische en organisatorische maatregelen om uw gegevens te beschermen tegen ongeoorloofde toegang, wijziging, openbaarmaking of vernietiging (HTTPS-versleuteling, toegangscontrole, regelmatige back-ups).`,
        ],
      },
      {
        heading: 'Wijziging van het beleid',
        body: [
          `Dit privacybeleid kan op elk moment worden gewijzigd om eventuele juridische of technische ontwikkelingen weer te geven. De datum van de laatste update staat onderaan de pagina.`,
        ],
      },
    ],
    updated: `Laatst bijgewerkt: ${COMPANY.lastUpdated}.`,
  },
};
